import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useCallback, useMemo } from "react";

import type {
  FieldPaginatedTableValueOptions,
  FilterType,
  SortableType,
} from "@/components/@types";
import { FieldTable } from "@/components/organisms/Field/FieldTable";
import { columnsToFilterDefinitions } from "@/components/organisms/UtilityBar/UtilityBar.utils";
import { getQueryVariables } from "@/hooks/useConnectionSetup";
import { useScope } from "@/hooks/useScope";

export type { FieldPaginatedTableValueOptions };

type ConnectionShape = {
  totalCount?: number;
  pageInfo: {
    hasNextPage: boolean;
    endCursor?: string | null;
  };
  edges: { node: unknown }[];
};

const NO_CHIPS: never[] = [];

export function FieldPaginatedTable({
  data,
  valueOptions,
  style,
  filters = NO_CHIPS,
  sortables = NO_CHIPS,
  cellSize,
}: {
  data?: unknown;
  valueOptions: FieldPaginatedTableValueOptions;
  style?: React.CSSProperties;
  /**
   * Chips over the table's columns - a group toolbar's - narrowing the rows
   * its connection gives, as a list view's chips narrow its own.
   */
  filters?: FilterType[];
  sortables?: SortableType[];
  /** The size the cells are set at; the table's own when not given. */
  cellSize?: "sm" | "md";
}) {
  const { activeEntityIdentifiers } = useScope();
  const { columns, connection } = valueOptions;
  const {
    query,
    queryKey,
    getVariables,
    pageSize: connectionPageSize,
  } = connection;
  const pageSize = connectionPageSize ?? 50;

  const variables = useMemo((): Record<string, unknown> => {
    const own = getVariables(data ?? {}, {
      entityIdentifiers: activeEntityIdentifiers ?? [],
    });
    // A search term is only there when the toolbar offered one.
    const { filter, order } = getQueryVariables({
      filterDefinitions: columnsToFilterDefinitions(columns),
      sortables,
      filters,
      searchable: true,
    });
    return {
      ...own,
      ...(filter && {
        filter: own.filter ? { all: [own.filter, filter] } : filter,
      }),
      ...(order && { order }),
    };
  }, [
    activeEntityIdentifiers,
    columns,
    data,
    filters,
    getVariables,
    sortables,
  ]);

  const entityIdentifiers = variables.entityIdentifiers as string[] | undefined;
  const skip = !entityIdentifiers?.length;

  const {
    data: queryData,
    loading,
    fetchMore,
    networkStatus,
  } = useQuery(query, {
    variables: { ...variables, first: pageSize },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip,
  });

  const connectionData = (
    queryData as Record<string, { data?: ConnectionShape }> | undefined
  )?.[queryKey]?.data;

  const rows = useMemo(
    () =>
      (connectionData?.edges?.map((edge) => edge.node) ?? []) as Record<
        string,
        unknown
      >[],
    [connectionData],
  );
  const totalCount = connectionData?.totalCount ?? 0;

  const loadNext = useCallback(() => {
    if (!connectionData?.pageInfo?.hasNextPage) return;
    void fetchMore({
      variables: {
        ...variables,
        first: pageSize,
        after: connectionData.pageInfo.endCursor,
      },
    });
  }, [connectionData, fetchMore, pageSize, variables]);

  return (
    <FieldTable
      value={rows}
      valueOptions={{ columns }}
      cellSize={cellSize}
      style={style}
      isLoading={!skip && loading && rows.length === 0}
      pagination={{
        loadNext,
        totalCount,
        isLoading: !skip && loading,
        networkStatus,
      }}
    />
  );
}
